import type { OutcomeRow, OutcomeTargetRow } from "./buildOutcomeRows";

/**
 * 確認済みを読み書きする。
 *
 * **記録は Web コンソールと共有する。** 単位は枠で、Web で付けたチェックがここでも見え、その逆も
 * 成り立つ。同じ成果物を 2 つの画面で確かめるので、記録が分かれていると数が合わなくなる
 * （[[2026-09-10 設計ノート アドイン 生成結果画面の結果情報対応]]）。
 *
 * 共有する以上、**キーの揃え方も同じにする。** Web の現行の一覧は 3 種類のキーを書いてきた。
 *
 * | キー | 由来 |
 * | :-- | :-- |
 * | `out:{位置}:{枠の識別子}` | 出力のある行 |
 * | `{枠の識別子}` | 旧キー・未充填の行 |
 * | `prompt:{担当 id}` | 出力先を持たない要確認事項の行 |
 */

const OUT_PREFIX = "out:";
const PROMPT_PREFIX = "prompt:";

/** `out:{位置}:{枠の識別子}` から枠の識別子を取り出す。他の形なら `null`。 */
function outKeyRef(key: string): string | null {
    if (!key.startsWith(OUT_PREFIX)) return null;
    const rest = key.slice(OUT_PREFIX.length);
    const sep = rest.indexOf(":");
    if (sep < 0) return null;
    return rest.slice(sep + 1);
}

/**
 * 枠が確認済みか。
 *
 * 枠の識別子そのもので記録されていれば確認済み。位置つきの旧い記録しか無い場合も、その枠に
 * ついた記録が 1 つでもあれば確認済みとみなす。**記録が残っている以上その枠は目を通されている**
 * ためで、「全部」を求めると位置つきの記録がいくつあったかを新しい器から知る手立てが無く、
 * 永遠に確認済みにならない。
 */
export function isTargetReviewed(target: OutcomeTargetRow, reviewed: ReadonlySet<string>): boolean {
    if (reviewed.has(target.checkKey)) return true;
    return Array.from(reviewed).some((key) => outKeyRef(key) === target.checkKey);
}

/** 行の配下で確認済みの枠の数。 */
export function reviewedCountOf(row: OutcomeRow, reviewed: ReadonlySet<string>): number {
    return row.targets.filter((t) => isTargetReviewed(t, reviewed)).length;
}

/**
 * 行が確認済みか。**配下の枠がすべて確認済みのときだけ**。
 *
 * 枠を持たない担当は、担当そのものを指す記録で見る。Web の `prompt:{担当 id}` と同じ形なので、
 * 記録がそのまま効く。
 */
export function isRowReviewed(row: OutcomeRow, reviewed: ReadonlySet<string>): boolean {
    if (row.targets.length === 0) return reviewed.has(PROMPT_PREFIX + row.assignmentId);
    return row.targets.every((t) => isTargetReviewed(t, reviewed));
}

/** 行が一部だけ確認済みか。 */
export function isRowPartlyReviewed(row: OutcomeRow, reviewed: ReadonlySet<string>): boolean {
    if (row.targets.length === 0) return false;
    const n = reviewedCountOf(row, reviewed);
    return n > 0 && n < row.targets.length;
}

/**
 * 枠の付け外し。
 *
 * **外すときは位置つきの旧い記録も一緒に消す。** 読む側が旧い記録を見るのに書く側が消さないと、
 * 外そうとしても外れず、しかも保存は全置換なので押すたびに書き戻される。
 */
export function toggleTargetKeys(
    reviewed: ReadonlySet<string>,
    targets: readonly OutcomeTargetRow[],
    checked: boolean
): Set<string> {
    const next = new Set(reviewed);
    for (const target of targets) {
        if (checked) {
            next.add(target.checkKey);
            continue;
        }
        next.delete(target.checkKey);
        Array.from(next).forEach((key) => {
            if (outKeyRef(key) === target.checkKey) next.delete(key);
        });
    }
    return next;
}

/** 行の付け外し。配下の枠をまとめて動かす。 */
export function toggleRowKeys(
    reviewed: ReadonlySet<string>,
    rows: readonly OutcomeRow[],
    checked: boolean
): Set<string> {
    let next = new Set(reviewed);
    for (const row of rows) {
        if (row.targets.length === 0) {
            const key = PROMPT_PREFIX + row.assignmentId;
            if (checked) next.add(key);
            else next.delete(key);
            continue;
        }
        next = toggleTargetKeys(next, row.targets, checked);
    }
    return next;
}

/** 確認済みの枠の数と枠の総数。**進捗の分母は枠の数**で、担当の数ではない。 */
export function countReviewedTargets(
    rows: readonly OutcomeRow[],
    reviewed: ReadonlySet<string>
): { done: number; total: number } {
    let done = 0;
    let total = 0;
    for (const row of rows) {
        if (row.targets.length === 0) {
            total += 1;
            if (isRowReviewed(row, reviewed)) done += 1;
            continue;
        }
        total += row.targets.length;
        done += reviewedCountOf(row, reviewed);
    }
    return { done, total };
}
